import { useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { UTILS_ADD_FORM_DATA } from 'src/app/constants/dispatcherId';
import { FEATURE_LINKOUT, useFeatureToggleData } from 'src/app/featureToggle';
import { dispatchJobLocationActions } from 'src/app/state/dispatcher/jobLocation';
// @ts-ignore
import utils from 'src/app/state/dispatcher/utils';
import { mapJobToDraftValue } from 'src/app/state/dispatcher/utils/addFormData/utils/mapper';
import { actionUpdateBrand } from 'src/app/state/modules/formData/brand';
import { actionUpdateListingDate } from 'src/app/state/modules/formData/listingDate';
import { actionUpdatePostChannel } from 'src/app/state/modules/session/postChannel';
import { actionUpdateProductFeatures } from 'src/app/state/modules/session/productFeatures';
import { log } from 'src/app/utils/logging';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { useHirerAccount } from 'src/app/views/hooks/useHirerAccount';
import { useQueryParams } from 'src/app/views/hooks/useQueryParams';
import { useGetJobDetailsLazyQuery } from 'src/graphql/generated';
import type AppState from 'src/types/appState';

import { useCanUpdateExpiryDate } from '../useCanUpdateExpiryDate';

export const useJobData = () => {
  const { advertiserNation, isDataCleanupComplete, seekAdvertiserId } =
    useSelector(
      (state: AppState) => ({
        advertiserNation: state.session.hirerAccount.nation,
        isDataCleanupComplete: state.app.isDataCleanupComplete,
        seekAdvertiserId: state.session.seekAdvertiserId,
      }),
      shallowEqual,
    );

  const dispatch = useDispatch();

  const [isJobDataLoading, setIsJobDataLoading] = useState(true);
  const { getJobIdParam } = useQueryParams();
  const { isLoading: hireAccountLoading } = useHirerAccount();
  const canUpdateExpiryDate = useCanUpdateExpiryDate();
  const { getLanguage } = useConfig();

  const hasLinkOutEnabled = useFeatureToggleData(FEATURE_LINKOUT);

  const populateFormData = (data: any) => {
    if (data) {
      // formData
      const mappedDraftData = mapJobToDraftValue({
        jobData: data.getJobDetails,
        advertiserNation,
        hasLinkOutEnabled: Boolean(hasLinkOutEnabled),
        flowType: 'edit',
        canUpdateExpiryDate,
      });
      dispatch(utils(UTILS_ADD_FORM_DATA, mappedDraftData));
      // brand
      dispatch(actionUpdateBrand(data.getJobDetails?.branding));
      // location
      dispatchJobLocationActions(dispatch, data.getJobDetails?.locations?.[0]);
      // listing date
      dispatch(actionUpdateListingDate(data.getJobDetails?.listingDate));
      // productFeatures
      dispatch(
        actionUpdateProductFeatures(data.getJobDetails?.productFeatures),
      );
      // postChannel
      dispatch(actionUpdatePostChannel(data.getJobDetails?.postChannel));
      setIsJobDataLoading(false);
    }
  };

  const [getJobDetails, { data, error }] = useGetJobDetailsLazyQuery({
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (data) {
      populateFormData(data);
    }

    if (error) {
      log.error('useJobData | GetJobDetails', {
        error,
        advertiserNation,
        hasLinkOutEnabled,
        isDataCleanupComplete,
        seekAdvertiserId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);

  useEffect(() => {
    const jobIdParam = getJobIdParam();

    if (jobIdParam) {
      // wait for hireAccountLoading because populateFormData depends on hirerAccount.hasLinkOutEnabled
      if (isDataCleanupComplete && seekAdvertiserId && !hireAccountLoading) {
        getJobDetails({
          variables: {
            advertiserId: seekAdvertiserId,
            jobId: jobIdParam,
            language: getLanguage(),
            contextualCountry: advertiserNation,
          },
        });
      }
    } else {
      setIsJobDataLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataCleanupComplete, seekAdvertiserId, hireAccountLoading]);

  return { loading: isJobDataLoading };
};
